import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import type {
  CaseRecord,
  CaseFilter,
  DashboardStats,
  DashboardStatsQuery,
  DashboardAnalyticsBundle,
} from "@/types";
import { buildAnalyticsBundleQueryParams } from "@/lib/dashboard-api";
import { PAGE_SIZE } from "@/lib/constants";
import config from "@/config";

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

function buildDashboardStatsQueryString(q: DashboardStatsQuery): string {
  const p = new URLSearchParams();
  if (q.product?.trim()) p.set("product", q.product.trim());
  if (q.bin?.trim()) p.set("bin", q.bin.trim());
  if (q.pcn?.trim()) p.set("pcn", q.pcn.trim());
  if (q.from?.trim()) p.set("from", q.from.trim());
  if (q.to?.trim()) p.set("to", q.to.trim());
  return p.toString();
}

// ─── Stats Hook ─────────────────────────────────────────────────────

export function useDashboardStats(query: DashboardStatsQuery = {}) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryString = useMemo(
    () => buildDashboardStatsQueryString(query),
    [query.product, query.bin, query.pcn, query.from, query.to]
  );
  const queryStringRef = useRef(queryString);
  queryStringRef.current = queryString;

  const fetchSeqRef = useRef(0);
  const statsAbortRef = useRef<AbortController | null>(null);

  const fetchStats = useCallback(async () => {
    statsAbortRef.current?.abort();
    const ac = new AbortController();
    statsAbortRef.current = ac;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const qs = queryStringRef.current;
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/stats${qs ? `?${qs}` : ""}`,
        { signal: ac.signal },
      );
      if (!res.ok) throw new Error(`Stats fetch failed: ${res.status}`);
      const data = await res.json();
      if (seq !== fetchSeqRef.current) return;
      setStats(data);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      if (isAbortError(err)) return;
      setError(err instanceof Error ? err.message : "Failed to fetch stats");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchStats();
    return () => {
      statsAbortRef.current?.abort();
    };
  }, [fetchStats, queryString]);

  return { stats, loading, error, refresh: fetchStats };
}

// ─── Analytics bundle (charts only; headline stats via useDashboardStats) ─

export function useDashboardAnalyticsBundle(
  caseFilters: DashboardStatsQuery,
  fromStatuses: string[],
  toStatuses: string[],
  refetchKey: number,
) {
  const [bundle, setBundle] = useState<
    Omit<DashboardAnalyticsBundle, "stats"> | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSeqRef = useRef(0);
  const bundleAbortRef = useRef<AbortController | null>(null);

  const fetchBundle = useCallback(async () => {
    bundleAbortRef.current?.abort();
    const ac = new AbortController();
    bundleAbortRef.current = ac;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = buildAnalyticsBundleQueryParams(
        caseFilters,
        fromStatuses,
        toStatuses,
      );
      const qs = params.toString();
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/analytics-bundle${qs ? `?${qs}` : ""}`,
        { signal: ac.signal },
      );
      if (!res.ok) throw new Error(`Analytics bundle failed: ${res.status}`);
      const data = (await res.json()) as Omit<DashboardAnalyticsBundle, "stats"> & {
        stats?: DashboardStats;
      };
      if (seq !== fetchSeqRef.current) return;
      const { stats: _s, ...rest } = data;
      setBundle(rest as Omit<DashboardAnalyticsBundle, "stats">);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      if (isAbortError(err)) return;
      setError(
        err instanceof Error ? err.message : "Failed to load dashboard analytics",
      );
      setBundle(null);
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [caseFilters, fromStatuses, toStatuses]);

  useEffect(() => {
    void fetchBundle();
    return () => {
      bundleAbortRef.current?.abort();
    };
  }, [fetchBundle, refetchKey]);

  return {
    bundle,
    transition: bundle?.transition ?? null,
    failReasonBreakdown: bundle?.failReasonBreakdown ?? null,
    vapiAssistant: bundle?.vapiAssistant ?? null,
    capped: bundle?.capped ?? false,
    cap: bundle?.cap,
    loading,
    error,
    refresh: fetchBundle,
  };
}

// ─── Cases Hook (paginated) ─────────────────────────────────────────

export function useDashboardCases(filter: CaseFilter) {
  const [cases, setCases] = useState<CaseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const filterRef = useRef(filter);
  const fetchSeqRef = useRef(0);

  const fetchPage = useCallback(
    async (append: boolean) => {
      const seq = ++fetchSeqRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCases([]);
        cursorRef.current = null;
      }
      setError(null);

      try {
        // "failed" isn't a backend filter — fetch "all" and filter client-side
        const apiFilter = filter === "failed" ? "all" : filter;
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          filter: apiFilter,
        });
        if (append && cursorRef.current) {
          params.set("starting_after", cursorRef.current);
        }
        const res = await fetch(`${config.API_BASE_URL}/api/dashboard/cases?${params}`);
        if (!res.ok) throw new Error(`Cases fetch failed: ${res.status}`);
        const data = await res.json();
        if (seq !== fetchSeqRef.current) return;
        // Map document_id → id for frontend compatibility
        const mapped = (data.cases || []).map((c: Record<string, unknown>) => ({
          ...c,
          id: c.document_id || c.id,
        }));
        setCases((prev) => (append ? [...prev, ...mapped] : mapped));
        setHasMore(data.has_more ?? false);
        cursorRef.current = data.next_cursor ?? null;
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        setError(err instanceof Error ? err.message : "Failed to fetch cases");
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [filter]
  );

  // Re-fetch when filter changes
  useEffect(() => {
    if (filterRef.current !== filter) {
      filterRef.current = filter;
    }
    fetchPage(false);
  }, [filter, fetchPage]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchPage(true);
    }
  }, [fetchPage, loadingMore, hasMore]);

  const refresh = useCallback(() => {
    fetchPage(false);
  }, [fetchPage]);

  return { cases, loading, loadingMore, error, hasMore, loadMore, refresh };
}
